import type { Stats } from './stats'
import { weekStart } from './stats'

// Home's pulse (components/home/HomeTab): one line about the household,
// written from what it actually does: the staple it buys most, a shopping
// streak, who keeps the list, how quick its trips are. Only what is true
// makes a line, and only kindly: a person is named for praise, never for
// something left undone. One line a day (pickPulse), the same all day.

export interface PulseInput {
  stats: Pick<Stats, 'empty' | 'staples' | 'weeks' | 'window' | 'adders'>
  members: { id: string; display_name: string }[]
  selfMemberId: string | null
  now: Date
}

/** `lead` + `mark` (highlighted) + `tail` read as one sentence; `sub` under it. */
export interface PulseLine { key: string; lead: string; mark: string; tail: string; sub: string }

const DAY = 86_400_000
/** A staple worth a line: bought at least this often in Stats' 90 days. */
const STAPLE_MIN = 4
/** A streak worth a line, in weeks. */
const STREAK_MIN = 3
/** Crowned only for this many items added in Stats' 30 days… */
const KEEPER_MIN = 5
/** …and a trip is "quick" at this many minutes or under, over at least this many trips. */
const QUICK_MAX_MIN = 25
const QUICK_MIN_TRIPS = 3

const short = (d: Date) => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })

/** Weeks in a row with a trip, ending this week, or last week while this one has none yet. */
export function weekStreak(weeks: Stats['weeks'], now: Date): number {
  const ordered = [...weeks].sort((a, b) => b.start.getTime() - a.start.getTime())
  const thisWeek = weekStart(now).getTime()
  let i = ordered[0]?.start.getTime() === thisWeek && ordered[0].trips === 0 ? 1 : 0
  let n = 0
  for (; i < ordered.length && ordered[i].trips > 0; i++) n++
  return n
}

function streakStart(weeks: Stats['weeks'], now: Date, n: number): Date {
  const thisWeek = weekStart(now)
  const current = weeks.find(w => w.start.getTime() === thisWeek.getTime())
  const last = new Date(thisWeek)
  if (!current || current.trips === 0) last.setDate(last.getDate() - 7)
  last.setDate(last.getDate() - 7 * (n - 1))
  return last
}

/** Whoever added the most, in a household of more than one, once it is a real number. */
export function listKeeper(adders: Stats['adders'], members: PulseInput['members'], selfMemberId: string | null) {
  if (members.length < 2) return null
  const top = [...adders].filter(a => a.memberId).sort((a, b) => b.items - a.items)[0]
  if (!top || top.items < KEEPER_MIN) return null
  const self = top.memberId === selfMemberId
  const name = self ? 'You' : members.find(m => m.id === top.memberId)?.display_name
  return name ? { name, items: top.items, self } : null
}

/** Before there is anything to say: a household just starting out. */
const STARTER: PulseLine = {
  key: 'starter', lead: 'Fresh list. ', mark: 'Big plans.', tail: '',
  sub: 'Add what you need. Matlist learns your household as you shop.',
}

export function pulseLines({ stats, members, selfMemberId, now }: PulseInput): PulseLine[] {
  const lines: PulseLine[] = []
  if (stats.empty) return [STARTER]
  const staple = stats.staples[0]
  if (staple && staple.times >= STAPLE_MIN) {
    lines.push({
      key: 'staple', lead: 'You’re a ', mark: staple.name.toLocaleLowerCase(), tail: ' household.',
      sub: `Bought ${staple.times} times in three months. Nobody is surprised.`,
    })
  }
  const streak = weekStreak(stats.weeks, now)
  if (streak >= STREAK_MIN) {
    lines.push({
      key: 'streak', lead: '', mark: `${streak} weeks`, tail: ' in a row.',
      sub: `Someone has shopped every week since ${short(streakStart(stats.weeks, now, streak))}.`,
    })
  }
  const keeper = listKeeper(stats.adders, members, selfMemberId)
  if (keeper) {
    lines.push({
      key: 'keeper', lead: '', mark: keeper.name, tail: keeper.self ? ' keep this list going.' : ' keeps this list going.',
      sub: `${keeper.items} items added in the last 30 days. Legend.`,
    })
  }
  const { avgMs, trips } = stats.window
  if (avgMs !== null && trips >= QUICK_MIN_TRIPS && avgMs <= QUICK_MAX_MIN * 60_000) {
    lines.push({
      key: 'quick', lead: 'In and out in ', mark: `${Math.round(avgMs / 60_000)} min`, tail: '.',
      sub: 'Your usual trip over the last 30 days. Quicker than most podcasts.',
    })
  }
  return lines.length ? lines : [STARTER]
}

/** Today's line: the same from morning to night, the next one tomorrow. */
export function pickPulse(lines: PulseLine[], now: Date): PulseLine {
  const day = Math.floor(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()) / DAY)
  return lines[day % lines.length]
}
